// SPDX-FileCopyrightText: 2023 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useProjectImportApi from 'hooks/useProjectImportApi';
import { useAppDispatch } from 'store/hooks';
import { setImportedProjectId, toggleImportDialog } from 'store/reducers/projectSettingsSlice';
import { fetchProjects } from 'store/reducers/projectsSlice';
import styled, { css } from 'styled-components';
import { useDropzone } from 'react-dropzone';
import classNames from 'classnames';
import { ToastByStatus } from 'components/treactui-template/organisms/toast/ToastMessage';
import { ImportProjectAckMessage } from '@tls/sw91-communication/types/com.api_db_access';
import { AcknowledgeStatus } from '@tls/sw91-communication/types/com.base';
import { Button, Colors, Dialog, FooterButton, InputFieldText, ProgressBar } from '@tls/treact-ui';
import { IconFolderOpen, IconLoading } from '@tls/treact-ui/icons';
import { FlagInformation } from '@tls/treact-ui/flags';
const InformationFlag = FlagInformation;
type Props = {
  saveOnEnter?: boolean;
};

export default function ImportProjectDialogContent({ saveOnEnter = false }: Readonly<Props>) {
  const { t } = useTranslation();

  const {
    uploadProject,
    cancelImport,
    cancelledRef,
    uploading,
    estimatedRemainingTime = 0,
    progress,
  } = useProjectImportApi();

  let saveIcon: React.JSX.Element | undefined = <IconLoading />;
  if (!uploading) {
    saveIcon = undefined;
  }

  const dispatch = useAppDispatch();

  const onClose = useCallback(() => {
    if (uploading) cancelImport();
    dispatch(toggleImportDialog());
  }, [cancelImport, dispatch, uploading]);

  const { open, getRootProps, getInputProps, acceptedFiles } = useDropzone();

  const onSave = useCallback(async () => {
    if (acceptedFiles.length === 0) return;

    let result;
    try {
      result = await uploadProject(acceptedFiles[0]);
    } catch (e) {
      if (cancelledRef.current) return;

      console.error('Unable to import project', e);
      ToastByStatus(500, t('projects.import.title'), t('projects.import.error'));
      return;
    }

    if (cancelledRef.current || result.status === 499) return;

    ToastByStatus(result.status, t('projects.import.title'), t('projects.import.error'), t('projects.import.success'));

    if (!result.data) return;
    const createResult = ImportProjectAckMessage.fromJSON(result.data);

    if (createResult?.messageHeader?.projectId && createResult?.status?.status === AcknowledgeStatus.SUCCESS) {
      dispatch(toggleImportDialog());
      dispatch(fetchProjects());
      dispatch(setImportedProjectId(createResult.messageHeader.projectId));
    }
  }, [acceptedFiles, cancelledRef, dispatch, t, uploadProject]);

  const handleEnterPress = useCallback(
    (event: KeyboardEvent) => {
      const { key } = event;

      if (key === 'Enter') {
        onSave();
      }
    },
    [onSave]
  );

  useEffect(() => {
    if (!saveOnEnter) return;
    window.addEventListener('keydown', handleEnterPress);

    return () => {
      window.removeEventListener('keydown', handleEnterPress);
    };
  }, [handleEnterPress, saveOnEnter]);

  let fileName = '';
  if (acceptedFiles.length > 0) {
    fileName = acceptedFiles[0].name;
  }

  const details =
    estimatedRemainingTime && estimatedRemainingTime > 0 ? (
      <Info>
        <InformationFlag width='1.25rem' />
        <span>{t('projects.import.estimated', { time: estimatedRemainingTime.toFixed(1) })}</span>
      </Info>
    ) : undefined;

  return (
    <>
      <Dialog.DialogContent>
        <TextInputSection data-testid='projects-import-dialog-content'>
          <InputFieldText
            flag='mandatory'
            label={t('projects.import.path')}
            value={fileName}
            disabled
            onClick={open}
            onValueChange={() => {}}
            data-testid='projects-import-dialog-path-input'
          />
          <div className='open'>
            <Button
              variant='secondary'
              icon={<IconFolderOpen />}
              onClick={open}
              disabled={uploading}
              data-testid='projects-import-dialog-browse-button'
            />
          </div>
        </TextInputSection>
        {!uploading ? (
          <UploadFile
            {...getRootProps({
              className: classNames({
                dropzone: true,
                disabled: uploading,
              }),
              'data-testid': 'projects-import-dialog-dropzone',
            })}
          >
            <input {...getInputProps()} />
            <span className='font-size_m'>{t('projects.import.description')}</span>
          </UploadFile>
        ) : (
          <LoaderContainer>
            <ProgressContainer>
              <span>{t('projects.import.progress')}</span>
              <ProgressBar percentage={progress ?? 0} />
              <span className='font-size_xl'>{progress?.toFixed(0) ?? 0}%</span>
            </ProgressContainer>
            {details}
          </LoaderContainer>
        )}
      </Dialog.DialogContent>
      <Dialog.Footer
        right={[
          <FooterButton
            key='cancel'
            label={t('cancel')}
            onClick={onClose}
            variant='secondary'
            data-testid='projects-import-dialog-cancel-button'
          />,
          <FooterButton
            key='import'
            icon={saveIcon}
            label={t('projects.import.shortTitle')}
            onClick={onSave}
            disabled={uploading || acceptedFiles.length === 0}
            data-testid='projects-import-dialog-confirm-button'
          />,
        ]}
      />
    </>
  );
}

const TextInputSection = styled.section`
  display: flex;
  column-gap: 1rem;

  .open {
    margin: auto;
  }
`;

const UploadFile = styled.section`
  margin: 0rem;
  border: 2px dashed ${Colors.trgrey6.hex};
  height: 100%;
  background-color: rgba(${Colors.trgrey1.rgb}, 0.7);
  text-align: center;

  display: flex;
  flex-direction: column;
  justify-content: center;

  ${({ theme }) => css`
    :hover {
      border-color: ${theme.trcolor2.hex};
      background: ${Colors.trwhite.hex};
      color: ${theme.trcolor1.hex};
    }

    &:disabled,
    &.disabled {
      border-color: rgba(${Colors.trgrey2.rgb}, 0.8);
      background-color: rgba(${Colors.trgrey1.rgb}, 0.2);
      color: rgba(${Colors.trgrey2.rgb}, 0.8);
    }
  `}
`;

const ProgressContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin: auto 0;
`;

const LoaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
`;

const Info = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  align-items: center;
`;
